// ═══════════════════════════════════════════════════════════════════════════
// Sesión y membresía — Etapa 3
//
// DOS PRINCIPIOS que gobiernan este módulo:
//
// 1. LA SESIÓN DEBE SOBREVIVIR SIN CONEXIÓN. La app es offline-first y se usa
//    en bodega y en campo. Una puerta que exija red convertiría cada zona sin
//    señal en una jornada perdida. supabase-js guarda la sesión en
//    localStorage; al arrancar se lee de ahí SIN tocar la red, y el token se
//    renueva solo cuando hay conexión.
//
// 2. ESTA ETAPA NO PROTEGE LOS DATOS TODAVÍA. Los datos siguen en
//    localStorage del dispositivo: quien tenga el equipo desbloqueado los ve,
//    haya login o no. Lo que la autenticación aporta AHORA es IDENTIDAD —
//    saber quién hace cada cosa — para que la Etapa 4 pueda atribuir ventas,
//    aplicar permisos por rol y mover los datos al servidor. Decirlo claro
//    evita una falsa sensación de seguridad.
// ═══════════════════════════════════════════════════════════════════════════

import { createClient } from "@supabase/supabase-js";
import { configSupabase } from "./config.js";
import { vecesFiltrada, avisoDeFiltrada } from "./filtradas.js";
import { olvidarSuplantacion } from "./suplantar.js";

const { url, anonKey } = configSupabase();

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,      // sesión en localStorage: sobrevive recargas
    autoRefreshToken: true,    // se renueva sola cuando hay red
    // SÍ se lee la sesión que viene en la liga: el correo de confirmación
    // llega como #access_token=… y sin esto la app lo ignoraba. El vendedor
    // confirmaba su cuenta, aterrizaba aquí y seguía fuera, sin entender por
    // qué. No toca el #t= del ticket: solo actúa si encuentra access_token.
    detectSessionInUrl: true,
    storageKey: "vt-sesion",
  },
});

// La membresía se cachea para poder arrancar sin red. Es dato de
// autorización, no un secreto: dice a qué vinícola perteneces y con qué rol.
const CLAVE_MEMBRESIA = "vt-membresia";

export function membresiaCacheada() {
  try { return JSON.parse(localStorage.getItem(CLAVE_MEMBRESIA) || "null"); }
  catch { return null; }
}

function guardarMembresia(m) {
  try {
    if (m) localStorage.setItem(CLAVE_MEMBRESIA, JSON.stringify(m));
    else localStorage.removeItem(CLAVE_MEMBRESIA);
  } catch { /* almacenamiento lleno o bloqueado: no es fatal */ }
}

/** Sesión guardada localmente. NO toca la red. */
export async function sesionLocal() {
  const { data } = await supabase.auth.getSession();
  return data?.session || null;
}

/**
 * Trae la membresía del servidor y la cachea.
 * Si no hay red, devuelve la cacheada: sin conexión se sigue trabajando.
 */
export async function cargarMembresia(userId) {
  if (!navigator.onLine) return membresiaCacheada();
  try {
    const { data, error } = await supabase
      .from("membership")
      .select("id, role, tenant_id, is_active, tenant:tenant_id (slug, name)")
      .eq("user_id", userId)
      .eq("is_active", true)
      .is("deleted_at", null)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    guardarMembresia(data || null);
    return data || null;
  } catch {
    // Fallo de red o de RLS: se cae a lo último conocido en vez de bloquear.
    return membresiaCacheada();
  }
}

export async function entrar(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: (email || "").trim(), password,
  });
  if (error) throw new Error(traducirError(error.message));
  // La membresía puede venir NULL y eso ya no es un error: es el estado de
  // quien llega con un ticket sin canjear. Antes se cerraba la sesión y se
  // le decía "pide acceso al administrador" — un callejón sin salida para
  // alguien que traía el acceso en la mano. Ahora el guardia lo manda a la
  // pantalla de canje, que sí explica qué hacer y permite salir.
  const membresia = await cargarMembresia(data.user.id);
  return { session: data.session, membresia };
}

/**
 * Crear cuenta. Solo se ofrece a quien llega con un ticket: el ticket es lo
 * que autoriza el alta. Sin él, la pantalla de acceso no registra a nadie —
 * una vinícola no quiere que cualquiera se dé de alta y quede dando vueltas.
 */
export async function registrar(email, password) {
  const { data, error } = await supabase.auth.signUp({
    email: (email || "").trim(), password,
  });
  if (error) throw new Error(traducirError(error.message));
  // Si el proyecto exige confirmar el correo, `session` viene null: la cuenta
  // existe pero todavía no puede entrar. Quien llama tiene que decirlo, no
  // fingir que ya entró.
  return { session: data.session, necesitaConfirmar: !data.session };
}

export async function salir() {
  guardarMembresia(null);
  // Si se estaba viendo como otro, la sesión apartada muere con la salida: una
  // sesión de dueño guardada y sin dueño a la vista es lo que no debe quedar.
  olvidarSuplantacion();
  try { await supabase.auth.signOut(); } catch { /* sin red: la sesión local ya se limpió */ }
}

export async function cambiarPassword(nueva) {
  const { error } = await supabase.auth.updateUser({ password: nueva });
  if (error) throw new Error(traducirError(error.message));
}

/** Suscribe a cambios de sesión (cierre en otra pestaña, expiración…). */
export function alCambiarSesion(callback) {
  const { data } = supabase.auth.onAuthStateChange((evento, sesion) => {
    if (evento === "SIGNED_OUT") guardarMembresia(null);
    callback(evento, sesion);
  });
  return () => data?.subscription?.unsubscribe();
}

const ETIQUETA_ROL = {
  owner: "Propietario", manager: "Gerente",
  agent: "Vendedor", accountant: "Contabilidad",
};
export const etiquetaRol = (r) => ETIQUETA_ROL[r] || r || "—";

function traducirError(msg = "") {
  const m = msg.toLowerCase();
  if (m.includes("invalid login credentials")) return "Correo o contraseña incorrectos.";
  if (m.includes("email not confirmed")) return "Falta confirmar el correo de la cuenta.";
  if (m.includes("password should be")) return "La contraseña es demasiado corta (mínimo 6 caracteres).";
  if (m.includes("failed to fetch") || m.includes("network"))
    return "Sin conexión. Para entrar por primera vez en este dispositivo necesitas internet.";
  if (m.includes("already registered") || m.includes("already been registered"))
    return "Ya existe una cuenta con ese correo. Entra con tu contraseña en vez de crear otra.";
  if (m.includes("rate limit")) return "Demasiados intentos. Espera un momento.";
  return msg;
}

// ═══════════════════════════════════════════════════════════════════════════
// Recuperar y cambiar la contraseña
//
// El operador, bloqueado en producción: "No encuentro la contraseña de mi
// cuenta... es una de las llamadas más comunes a soporte técnico y no tenemos
// un método automático para recuperar la contraseña."
//
// Lo había señalado como el ticket número uno de un SaaS chico, y el sistema
// no lo tenía. Que la primera persona en toparse con ello sea el dueño —el
// único que puede pedirme a mí que le genere un enlace— es suerte: un vendedor
// en campo un sábado se habría quedado fuera sin nadie a quien llamar.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Manda el correo de recuperación.
 *
 * `redirectTo` apunta a esta misma app: Supabase devuelve al usuario con una
 * sesión de recuperación y ahí se le pide la contraseña nueva.
 */
export async function pedirRecuperacion(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(
    (email || "").trim(), { redirectTo: window.location.origin + window.location.pathname });
  if (error) throw new Error(traducirError(error.message));
  // NO se distingue entre "se mandó" y "ese correo no existe": decirlo
  // permitiría averiguar quién tiene cuenta probando correos.
  return true;
}

/** El mínimo. Doce, y no ocho, porque el largo es lo que de verdad pesa: cada
 *  carácter multiplica el trabajo de adivinar mucho más que cualquier regla de
 *  "una mayúscula y un número". El mismo mínimo está puesto en el servidor
 *  (`password_min_length`), que es donde no se puede saltar. */
export const LARGO_MINIMO = 12;

/**
 * Fija una contraseña nueva. Exige sesión — la normal o la de recuperación.
 *
 * Se comprueba contra las brechas conocidas ANTES de mandarla. Supabase ofrece
 * esa comprobación pero solo en planes de paga; el servicio que usa por dentro
 * —HaveIBeenPwned— es público y gratuito, así que se pregunta desde aquí. La
 * contraseña no sale de la máquina: ver `auth/filtradas.js`.
 *
 * Si no hay red, se deja pasar. Alguien sin señal tiene que poder cambiar su
 * contraseña; una comprobación de higiene no puede volverse un candado.
 */
export async function cambiarContrasena(nueva) {
  if (!nueva || nueva.length < LARGO_MINIMO) {
    throw new Error(`La contraseña debe tener al menos ${LARGO_MINIMO} caracteres.`);
  }
  const { veces } = await vecesFiltrada(nueva);
  const aviso = avisoDeFiltrada(veces);
  if (aviso) throw new Error(aviso);

  const { error } = await supabase.auth.updateUser({ password: nueva });
  if (error) throw new Error(traducirError(error.message));
  return true;
}

/**
 * ¿Venimos de un enlace de recuperación?
 *
 * Supabase deja `type=recovery` en el fragmento. Se mira ANTES de que el
 * cliente lo consuma; si ya lo consumió, queda la marca en sessionStorage.
 */
export function esRecuperacion() {
  try {
    if (sessionStorage.getItem("vt-recuperando") === "1") return true;
    const h = (window.location.hash || "").replace(/^#/, "");
    if (new URLSearchParams(h).get("type") === "recovery") {
      sessionStorage.setItem("vt-recuperando", "1");
      return true;
    }
  } catch { /* sin almacenamiento */ }
  return false;
}

export function terminarRecuperacion() {
  try { sessionStorage.removeItem("vt-recuperando"); } catch { /* nada */ }
}
